import { useCallback, useRef } from "react";
import { useMutation } from "@apollo/client/react";
import { gql } from "../__generated__";
import type { AnswerSourcing } from "../components/LowConfidenceBanner";

const RECORD_ANSWER_USE = gql(`
  mutation RecordAnswerUse($messageId: UUID!, $revisionId: UUID) {
    proposedAnswerInteractionRecordUse(messageId: $messageId, revisionId: $revisionId)
  }
`);

/**
 * "Staff used the draft": call the returned function from every action that
 * puts the proposed answer into a reply (copy, insert, send, hand-off).
 *
 * At most one use per message per mount, so repeated copies and double clicks
 * count once. A copied SEARCH_BYPASS courtesy reply is not an answer use, so
 * the product KPIs keep counting answers to customer requests only; a
 * NO_SOURCES answer offers no use at all. `revisionId` is the revision on
 * screen, sent rather than looked up server-side because the pointer can move
 * between the action and the request arriving. Fire-and-forget: a failed
 * request never breaks the copy.
 */
export function useRecordAnswerUse(
  messageId: string | null | undefined,
  revisionId: string | null | undefined,
  answerSourcing: AnswerSourcing | null,
): () => void {
  const [record] = useMutation(RECORD_ANSWER_USE);
  const recordedForMessageRef = useRef<string | null>(null);

  return useCallback(() => {
    if (!messageId || recordedForMessageRef.current === messageId) return;
    if (answerSourcing === "NO_SOURCES" || answerSourcing === "SEARCH_BYPASS") return;
    recordedForMessageRef.current = messageId;
    record({ variables: { messageId, revisionId: revisionId ?? null } }).catch(
      (error: unknown) => {
        console.warn("Failed to record answer use", error);
      },
    );
  }, [messageId, revisionId, answerSourcing, record]);
}
